import React from 'react'
import { AlertCircle, CheckCircle2, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StepStatus = 'complete' | 'current' | 'upcoming' | 'error'

export interface PostCreateStep {
  id: string
  number: number
  title: string
  description?: string
  status: StepStatus
  badge?: string
}

interface PostCreateStepperProps {
  steps: PostCreateStep[]
  activeStepId?: string
  onStepSelect?: (stepId: string) => void
}

export function PostCreateStepper({
  steps,
  activeStepId,
  onStepSelect
}: PostCreateStepperProps) {
  if (!steps.length) return null

  return (
    <div className="rounded-xl border border-border/50 bg-card/80 px-3 py-3 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-stretch">
        {steps.map(step => {
          const isActive = step.id === activeStepId
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onStepSelect?.(step.id)}
              className={cn(
                'flex min-w-[240px] flex-1 items-start gap-3 rounded-lg border px-3 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-primary/30',
                step.status === 'complete' && 'border-emerald-200 bg-emerald-50/60',
                step.status === 'error' && 'border-red-200 bg-red-50/70',
                step.status === 'current' && 'border-primary/60 bg-primary/5',
                step.status === 'upcoming' && 'border-border/60',
                !isActive && 'opacity-90 hover:opacity-100'
              )}
            >
              <StatusIcon status={step.status} number={step.number} />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p
                    className={cn(
                      'text-sm font-semibold',
                      step.status === 'error' && 'text-red-700',
                      step.status === 'complete' && 'text-emerald-700'
                    )}
                  >
                    {step.title}
                  </p>
                  {step.badge && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-2xs uppercase tracking-wide text-muted-foreground">
                      {step.badge}
                    </span>
                  )}
                </div>
                {step.description && (
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function StatusIcon({ status, number }: { status: StepStatus; number: number }) {
  if (status === 'complete') {
    return <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
  }

  if (status === 'error') {
    return <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
  }

  if (status === 'current') {
    return <Circle className="mt-0.5 h-5 w-5 text-primary" />
  }

  return (
    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center text-xs font-semibold text-muted-foreground">
      {number}
    </span>
  )
}

export default PostCreateStepper
